import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { generateClassificationTrees } from '../data/classificationTrees';

export type GroupingOption = 'entity' | 'assetClass' | 'sector' | 'portfolio' | 'classificationTree';

export interface GroupingConfig {
  id: string;
  label: string;
  type: GroupingOption;
  treeId?: number;
  level?: number;
}

interface SortableItemProps {
  grouping: GroupingConfig;
  index: number;
  onRemove: (id: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ grouping, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: grouping.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        bgcolor: isDragging ? 'primary.light' : 'white',
        borderRadius: 1,
        border: '1px solid',
        borderColor: isDragging ? 'primary.main' : 'grey.300',
        opacity: isDragging ? 0.8 : 1
      }}
    >
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
        <DragIndicatorIcon color="action" />
      </Box>
      
      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
        {index + 1}. {grouping.label}
      </Typography>
      
      <Box sx={{ flexGrow: 1 }} />
      
      <IconButton
        size="small"
        onClick={() => onRemove(grouping.id)}
        color="error"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

interface GroupingSelectorProps {
  selectedGroupings: GroupingConfig[];
  onGroupingsChange: (groupings: GroupingConfig[]) => void;
}

// Generate available groupings including classification tree levels
const generateAvailableGroupings = (): GroupingConfig[] => {
  const baseGroupings: GroupingConfig[] = [
    { id: 'entity', label: 'Entity', type: 'entity' },
    { id: 'assetClass', label: 'Asset Type', type: 'assetClass' },
    { id: 'sector', label: 'Sector', type: 'sector' },
    { id: 'portfolio', label: 'Portfolio', type: 'portfolio' }
  ];

  const classificationGroupings: GroupingConfig[] = [];
  
  // Get all classification trees
  const trees = generateClassificationTrees();
  
  trees.forEach(tree => {
    if (tree.items) {
      // Get all levels including the deepest (leaf) level
      const levels = new Set<number>();
      tree.items.forEach(item => levels.add(item.level));
      const sortedLevels = Array.from(levels).sort();
      
      // Include all levels (both non-leaf and leaf levels)
      sortedLevels.forEach(level => {
        classificationGroupings.push({
          id: `${tree.name.toLowerCase()}_level${level}`,
          label: `${tree.name}: Level ${level}`,
          type: 'classificationTree',
          treeId: tree.id,
          level: level
        });
      });
    }
  });

  return [...baseGroupings, ...classificationGroupings];
};

const availableGroupings: GroupingConfig[] = generateAvailableGroupings();

const GroupingSelector: React.FC<GroupingSelectorProps> = ({
  selectedGroupings,
  onGroupingsChange
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddGrouping = (groupingId: string) => {
    const grouping = availableGroupings.find(g => g.id === groupingId);
    if (grouping && !selectedGroupings.find(g => g.id === groupingId)) {
      onGroupingsChange([...selectedGroupings, grouping]);
    }
    setAnchorEl(null); // Close the menu
  };

  const handleRemoveGrouping = (groupingId: string) => {
    onGroupingsChange(selectedGroupings.filter(g => g.id !== groupingId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = selectedGroupings.findIndex(item => item.id === active.id);
      const newIndex = selectedGroupings.findIndex(item => item.id === over?.id);

      onGroupingsChange(arrayMove(selectedGroupings, oldIndex, newIndex));
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const availableOptions = availableGroupings.filter(
    ag => !selectedGroupings.find(sg => sg.id === ag.id)
  );

  // Generate summary for accordion header
  const getGroupingSummary = () => {
    if (selectedGroupings.length === 0) {
      return "No grouping applied";
    }
    if (selectedGroupings.length === 1) {
      return `Grouped by ${selectedGroupings[0].label}`;
    }
    return `Grouped by ${selectedGroupings[0].label} → ${selectedGroupings.slice(1).map(g => g.label).join(' → ')}`;
  };

  return (
    <Box sx={{ mb: 0 }}>
      <Accordion defaultExpanded={false} sx={{ boxShadow: 0, border: '1px solid', borderColor: 'grey.300', bgcolor: 'white' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: 'white',
            '&:hover': { bgcolor: 'grey.50' },
            minHeight: 48,
            '& .MuiAccordionSummary-content': {
              alignItems: 'center'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'medium', minWidth: 'fit-content' }}>
              Group By:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getGroupingSummary()}
            </Typography>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ pt: 2 }}>
          {selectedGroupings.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Active Groupings (in hierarchy order):
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Drag to reorder
                </Typography>
              </Box>
              
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={selectedGroupings.map(g => g.id)} strategy={verticalListSortingStrategy}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {selectedGroupings.map((grouping, index) => (
                      <SortableItem
                        key={grouping.id}
                        grouping={grouping}
                        index={index}
                        onRemove={handleRemoveGrouping}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>

              <Box sx={{ display: 'flex', justifyContent: 'flex-start', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleClick}
                  disabled={availableOptions.length === 0}
                  size="medium"
                  sx={{ minWidth: 140 }}
                >
                  Add Grouping
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No groupings selected. Add a grouping to organize your data.
              </Typography>
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleClick}
                size="medium"
              >
                Add Your First Grouping
              </Button>
            </Box>
          )}

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'add-grouping-button',
            }}
          >
            {availableOptions.map((option) => (
              <MenuItem key={option.id} onClick={() => handleAddGrouping(option.id)}>
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default GroupingSelector;
